import type { ConversationRouterState } from "@mirage/service-conversation/router_state_types";
import { CtaSource } from "@mirage/service-conversation/router_state_types";
import { buildConversationRouterState } from "@mirage/service-conversation/utils/buildConversationRouterState";
import { convertFssEntityToChatSource } from "@mirage/service-conversation/utils/convertFssEntityToChatSource";
import { useConversationEvents } from "@mirage/service-fss-bridge/hooks/bridgeObservers";
import {
  ConversationSource,
  type ConversationEvent,
} from "@mirage/service-fss-bridge/service/bridge_types";
import { tagged } from "@mirage/service-logging";
import { useMountEffect } from "@react-hookz/web";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const logger = tagged("[fss] useConversationEventOrchestrator");

/**
 * This hook is responsible for orchestrating the conversation events. It listens to events from the
 * FSS bridge and builds the appropriate router state to navigate to the appropriate conversation.
 */
export const useConversationEventOrchestrator = ({
  onRequestNewSession,
}: {
  onRequestNewSession: (routerState?: ConversationRouterState) => void;
}) => {
  const event = useConversationEvents();
  const navigate = useNavigate();

  useMountEffect(() => {
    logger.info("initializing conversation event listener");
  });

  useEffect(() => {
    if (!event) {
      return;
    }

    logger.info("Received events: ", event);
    let routerState: ConversationRouterState | undefined;
    switch (event.source) {
      case ConversationSource.FssUnscoped: {
        routerState = buildConversationRouterState(CtaSource.FssUnscoped, {});
        break;
      }
      case ConversationSource.FssSerpQuery: {
        routerState = buildConversationRouterState(CtaSource.FssAskQuery, {
          message: event.data.searchQuery,
          scenarioMeta: {
            type: "fss_serp_query",
            query: event.data.searchQuery,
          },
        });
        break;
      }
      case ConversationSource.FssSerpTypeahead: {
        routerState = buildConversationRouterState(
          CtaSource.FssAskFromSuggestion,
          {
            message: event.data.searchQuery,
            agentIds: ["conversations"],
            chatSources:
              event.data.files?.map(convertFssEntityToChatSource) ?? [],
            scenarioMeta: {
              type: "fss_serp_query",
              query: event.data.searchQuery,
            },
          },
        );
        break;
      }
      case ConversationSource.FssFile: {
        if (event.agentIds?.includes("summarize")) {
          routerState = buildConversationRouterState(
            CtaSource.FssFileSummarize,
            {
              chatSources: event.data.files.map(convertFssEntityToChatSource),
              scenarioMeta: {
                type: "fss_file",
                files: event.data.files,
              },
            },
          );
        } else {
          routerState = buildConversationRouterState(CtaSource.FssAskWithFile, {
            chatSources: event.data.files.map(convertFssEntityToChatSource),
            scenarioMeta: {
              type: "fss_file",
              files: event.data.files,
            },
          });
        }
        break;
      }
      case ConversationSource.FssFolder:
        if (event.agentIds?.includes("summarize")) {
          routerState = buildConversationRouterState(
            CtaSource.FssFolderSummarize,
            {
              chatSources: event.data.folders.map(convertFssEntityToChatSource),
              scenarioMeta: {
                type: "fss_folder",
                folder: event.data.folders[0],
              },
            },
          );
        } else {
          routerState = buildConversationRouterState(
            CtaSource.FssAskWithFolder,
            {
              chatSources: event.data.folders.map(convertFssEntityToChatSource),
              scenarioMeta: {
                type: "fss_folder",
                folder: event.data.folders[0],
              },
            },
          );
        }
        break;
      default:
        event satisfies never;
        throw new Error(
          `Unexpected source type: ${(event as ConversationEvent).source}`,
        );
    }
    if (routerState) {
      if (event.data.createNewSession) {
        onRequestNewSession(routerState);
        return;
      }
      // "." represents the current path, so this will not navigate to a different page
      navigate(".", {
        state: routerState,
      });
    }
  }, [event, navigate, onRequestNewSession]);
};
